import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#4B2D6E] text-white py-8">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/careers" className="inline-flex items-center text-white/80 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Careers
          </Link>
          <h1 className="text-3xl font-bold font-[Lato]">Privacy Policy</h1>
          <p className="text-white/70 mt-2">SeniorPeerConnect™ — a product of JDI Business Solutions, LLC</p>
          <p className="text-white/60 text-sm mt-1">Last Updated: March 26, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8 text-[#333333] leading-relaxed">
        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">1. Introduction</h2>
          <p>
            SeniorPeerConnect™ is a product of JDI Business Solutions, LLC ("we," "us," or "our"). We are committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, 
            apply for employment, or interact with our services, including our applicant tracking system powered by SeniorPeerConnect™.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">2. Information We Collect</h2>
          <p className="mb-3">We may collect the following types of information:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Personal Information:</strong> Name, email address, phone number, mailing address, and other contact details you provide during the application process.</li>
            <li><strong>Employment Information:</strong> Resume, work history, certifications, availability, references, and responses to screening questions.</li>
            <li><strong>Communication Data:</strong> Records of SMS messages, emails, and other communications between you and our team.</li>
            <li><strong>Device & Usage Information:</strong> IP address, browser type, pages visited, and interaction data collected automatically through cookies and similar technologies.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">3. How We Use Your Information</h2>
          <p className="mb-3">We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Process and evaluate your employment application</li>
            <li>Communicate with you about your application status, interview scheduling, and onboarding</li>
            <li>Send SMS and email notifications related to the hiring process</li>
            <li>Conduct background checks (with your consent)</li>
            <li>Improve our recruitment process and website functionality</li>
            <li>Comply with legal and regulatory obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">4. SMS/Text Messaging</h2>
          <p className="mb-3">
            By providing your phone number during the application process, you consent to receive SMS/text messages from 
            Seniors Helping Seniors® Union & Somerset County NJ (via SeniorPeerConnect™, operated by JDI Business Solutions, LLC) related to your application, including:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Application status updates</li>
            <li>Interview scheduling and reminders</li>
            <li>Offer letter notifications</li>
            <li>Onboarding instructions</li>
          </ul>
          <p className="mt-3">
            <strong>Message frequency varies.</strong> Message and data rates may apply. You may opt out at any time by replying 
            <strong> STOP</strong> to any message. Reply <strong>HELP</strong> for assistance. Your consent to receive SMS is not 
            a condition of employment.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">5. Information Sharing</h2>
          <p className="mb-3">
            We do <strong>not</strong> sell, rent, or trade your personal information to third parties for marketing purposes. 
            We may share your information with:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Service Providers:</strong> Third-party services that assist with our operations (e.g., communication platforms, background check providers), bound by confidentiality agreements.</li>
            <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
            <li><strong>Business Operations:</strong> Within our franchise network as needed for employment purposes.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">6. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized 
            access, alteration, disclosure, or destruction. This includes encrypted data transmission, secure database storage, 
            and role-based access controls. However, no method of transmission over the internet is 100% secure.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">7. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, 
            or as required by law. Application data for candidates who are not hired is retained for up to 12 months and then 
            securely deleted unless you request earlier removal.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">8. Your Rights</h2>
          <p className="mb-3">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Opt out of SMS communications at any time</li>
            <li>Withdraw consent for data processing</li>
          </ul>
          <p className="mt-3">To exercise any of these rights, please contact us using the information below.</p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the 
            new policy on this page with an updated "Last Updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">10. Contact Us</h2>
          <p>If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:</p>
          <div className="mt-3 p-4 bg-[#F5F5F5] rounded-lg">
            <p className="font-semibold">JDI Business Solutions, LLC</p>
            <p className="text-sm text-[#666] mt-0.5">d/b/a SeniorPeerConnect™</p>
            <p className="text-sm text-[#666] mt-1">Email: renee@shsnorthplainfield.com</p>
            <p className="text-sm text-[#666]">Phone: (908) 830-1750</p>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div className="border-t py-6 text-center text-sm text-[#999]">
        <p>&copy; {new Date().getFullYear()} SeniorPeerConnect™, a product of JDI Business Solutions, LLC. All rights reserved.</p>
        <div className="mt-2 flex justify-center gap-4">
          <Link to="/privacy-policy" className="text-[#4B2D6E] hover:underline">Privacy Policy</Link>
          <Link to="/terms-and-conditions" className="text-[#4B2D6E] hover:underline">Terms & Conditions</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
